<script>
import ModelChildIndex from "../../models/children/index";

export default {
  extends: ModelChildIndex,
  props: {
    cardTitle: {
      default() {
        return this.lodash.startCase(this.$t("sale_notes.plural"));
      }
    },
    resourceRoute: { default: "sale_notes" },
    modelVariable: { default: "sale_note" },
    tableMinHeight: { type: Number, default: 85.5 }, // 42.5 + 43
    tableHeight: { type: Number, default: 214.5 }, // 42.5 + (43 * 4)
    denyCreate: { type: Boolean, default: true },
    hideTableHeader: { type: Boolean, default: true },
    values: {
      type: Array,
      default() {
        return [];
      }
    },
    showProps: {
      default() {
        return {
          cardTitle: this.lodash.startCase(this.$t("sale_notes.singular")),
          resourceRoute: { default: "sale_notes" },
          modelVariable: "sale_note",
          denyEdit: true,
          denyDelete: true
        };
      }
    }
  },
  data() {
    return {
      sortBy: "ordered_at",
      sortDesc: true
    };
  },
  computed: {
    columns() {
      return {
        ["sale_note"]: [
          {
            key: "id",
            label: this.lodash.startCase(this.$t(`${this.resourceRoute}.id`)),
            sortable: true,
            thStyle: { width: "150px" },
            component: {
              props: ["item"],
              computed: {
                customLabel() {
                  return `${this.item.type_name}-${this.item.id}`;
                }
              },
              template: /*html*/ `
                <div>{{ customLabel }}</div>`
            }
          },
          {
            key: "unload_id",
            label: this.lodash.startCase(this.$t(`tickets.sale_order_id`)),
            component: {
              props: ["item"],
              computed: {
                customLabel() {
                  return `${this.item.type_code}-${this.item.id}`;
                }
              },
              template: /*html*/ `
                <div>
                    {{item.id}}
                </div>
              `
            }
          },
          {
            key: "ordered_at",
            label: this.lodash.startCase("tanggal laporan"),
            thStyle: { minWidth: "100px" },
            formatter: value => (value ? this.$moment(value).format("DD MMM YYYY") : "-")
          },
          {
            key: "purchase_order_partner_name",
            label: this.lodash.startCase(this.$t(`${this.resourceRoute}.partner`)),
            thStyle: { minWidth: "140px" },
            formatter: value => value || "-"
          },
          {
            key: "processing_partner",
            label: this.lodash.startCase("tujuan"),
            thStyle: { minWidth: "140px" },
            formatter: value => value || "-"
          },
          {
            key: "sale_item_total_quantity",
            label: this.lodash.startCase("biomassa"),
            tdClass: "text-end",
            style: { minWidth: "100px" },
            formatter: value => `${this.$numeral(value / 1000).format("0,0.[000]")} ${this.$t("units.ton")}`
          },
          {
            key: "total_revenue",
            label: this.lodash.startCase(this.$t(`${this.resourceRoute}.revenue`)),
            tdClass: "text-end",
            style: { minWidth: "150px" },
            formatter: value => this.$numeral(value).format("$ 0,0.[000]")
          },
          {
            key: "due_at",
            label: this.lodash.startCase(this.$t(`${this.resourceRoute}.due_at`)),
            thStyle: { minWidth: "120px" },
            component: {
              props: ["item"],
              computed: {
                dueAt() {
                  let duration =
                    this.item.type === "hybrid"
                      ? this.item.sale_items?.[0]?.purchase_order?.duration
                      : this.item.duration;
                  if (!duration) {
                    duration = 0;
                  }
                  const orderedAt = this.$moment(this.item.ordered_at);

                  return orderedAt.add(duration, "days").format("DD MMMM YYYY");
                }
              },
              template: `
              <div>{{dueAt}}</div>
              `
            }
          },
          {
            key: 'odoo_sale_order.state',
            label: this.lodash.startCase(this.$t("sale_notes.odoo_sale_orders.state")),
            component: {
              props: ["item"],
              computed: {
                translatedState() {
                  let state = this.item.odoo_sale_order?.state
                  if (!state) {
                    return '-'
                  }
                  return this.$t(`sale_notes.odoo_sale_orders.states.${state}`)
                },
                badgeClass() {
                  let state = this.item.odoo_sale_order?.state

                  if (!state) {
                    return {'badge': true, 'bg-light': true}
                  }

                  if (state === 'purchase') {
                    return {'status-badge': true, 'badge': true, 'bg-success': true}
                  }  else if (state === 'to approve') {
                    return {'badge': true, 'bg-warning': true}
                  }

                  return {'badge': true, 'bg-light': true}
                },
              },
              template:`
                <status-badge
                    v-if="translatedState != '-'"
                    entity="odoo_sale_order"
                    type="status"
                    :status="item.odoo_sale_order.state"
                ></status-badge>
                <span v-else>-</span>
              `
            },
          },
          {
            key: 'odoo_sale_order.invoice_status',
            label: this.lodash.startCase(this.$t("sale_notes.odoo_sale_orders.invoice_status")),
            component: {
              props: ["item"],
              computed: {
                translatedInvoiceStatus() {
                  let invoiceStatus = this.item.odoo_sale_order?.invoice_status
                  if (!invoiceStatus) {
                    return '-'
                  }
                  return this.$t(`sale_notes.odoo_sale_orders.invoice_statuses.${invoiceStatus}`)
                },
                badgeClass() {
                  let invoiceStatus = this.item.odoo_sale_order?.invoice_status

                  if (!invoiceStatus) {
                    return {'badge': true, 'bg-light': true}
                  }

                  if (invoiceStatus === 'invoiced') {
                    return {'status-badge': true, 'badge': true, 'bg-success': true}
                  }  else if (invoiceStatus === 'to invoice') {
                    return {'badge': true, 'bg-warning': true}
                  }

                  return {'badge': true, 'bg-light': true}
                },
              },
              template:`
                <status-badge
                    v-if="translatedInvoiceStatus != '-'"
                    entity="odoo_sale_order"
                    type="invoice_status"
                    :status="item.odoo_sale_order.invoice_status"
                ></status-badge>
                <span v-else>-</span>
              `
            },
          },
        ]
      };
    }
  },
  methods: {
    store(value) {
      return this.$axios.post(`/tickets/${this.$route.params.key}/trips`, value);
    },
    update(value) {
      return this.$axios.put(`/tickets/${this.$route.params.key}/trips/${this.getModelKey(value)}`, value);
    },
    delete(value) {
      return this.$axios.delete(`/tickets/${this.$route.params.key}/trips/${this.getModelKey(value)}`);
    },
    fetchModels(page = 1) {
      return this.$axios
        .get(`/sale_orders?ticket_id__eq=${this.$route.params.key}`, {
          params: {
            page: page,
            per_page: this.perPage,
            search: this.search,
            sort: `${this.sortBy},${this.sortDesc ? "desc" : "asc"}`,
            with: [
              "customer",
              "odoo_sale_order.odoo_sale_order_lines",
              "purchase_order.customer",
              "purchase_order.farmer",
              "purchase_order.purchase_payments",
              "sale_items.purchase_order.customer",
              "sale_items.purchase_order.farmer",
              "sale_items.purchase_item.purchase_order.customer",
              "sale_items.purchase_item.purchase_order.farmer",
              "sale_items.trip_item.purchase_item",
              "sale_items.trip_item.purchase_order.customer",
              "sale_items.trip_item.purchase_order.farmer",
              "ticket",
              "trip.purchase_order",
              "trip.customer",
              "trip.driver",
              "trip.vehicle",
              "sales_reports",
              "sale_payments"
            ].join(","),
            ...this.filterParams
          }
        })
        .then(response => {
          const {
            data: { data, meta }
          } = response;
          this.models = data.map(datum => {
            let purchaseOrders = [];
            datum.sale_items.forEach(saleItem => {
              if (!this.lodash.isEmpty(saleItem.purchase_item)) {
                purchaseOrders.push(saleItem.purchase_item.purchase_order);
              }
            });
            const purchaseOrderPartner = this.purchaseOrderPartnerName(purchaseOrders);
            return {
              ...datum,
              purchase_order_partner_name: purchaseOrderPartner,
              partner_names: this.partnerNames(purchaseOrderPartner, datum),
              purchase_order_ordered_at: this.purchaseOrderOrderedAt(purchaseOrders),
              processing_partner: this.processingPartner(datum)
            };
          });
          this.page = Number(meta.current_page);
          this.perPage = Number(meta.per_page);
          this.from = Number(meta.from);
          this.total = Number(meta.total);

          this.stats = [];

          return response;
        })
        .catch(error => {
          this.failedAlert(error);
          return null;
        });
    },
    purchaseOrderPartnerName(purchaseOrders) {
      let uniquerCustomers = [...new Set(purchaseOrders.map(purchaseOrder => purchaseOrder.customer?.name))];

      if (uniquerCustomers.length > 1) return `${uniquerCustomers[0]}, ${this.$t("et_al")}`;
      else if (uniquerCustomers.length === 1) return uniquerCustomers[0];
      else return "-";
    },
    partnerNames(purchaseOrderPartner, saleOrder) {
      let processingPartner = saleOrder.buyer_name || saleOrder.trip?.customer?.name || null;
      if (saleOrder.type === "hybrid") processingPartner = saleOrder.buyer_name;
      if (!processingPartner) return purchaseOrderPartner;
      return `${purchaseOrderPartner} ke ${processingPartner}`;
    },
    processingPartner(saleOrder) {
      let partner = saleOrder.buyer_name || saleOrder.trip?.customer?.name || null;
      if (saleOrder.type === "hybrid") partner = saleOrder.buyer_name;
      return partner;
    },
    purchaseOrderOrderedAt(purchaseOrders) {
      return [...new Set(purchaseOrders.map(purchaseOrder => purchaseOrder.ordered_at))];
    }
  }
};
</script>

